/**
 * cta.ts — Categoria "Chamada para ação" (10 modelos premium)
 *
 * Convites para o próximo passo, montados em seções com camadas com
 * títulos, botões, selos, números e fotos do acervo compartilhado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { AVATARES, FOTO, WEB, b } from './_shared';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'cta.faixa-escura',
    category: 'cta',
    label: 'Faixa escura com botão',
    description: 'Bloco escuro com brilho e um convite direto.',
    preview: 'cta',
    outputs: WEB,
    order: 1,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '900px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', radius: 'xl', motion: 'fade-up' },
        [
          b('ui.title', { text: 'Pronto para começar?', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          b('ui.paragraph', { text: 'Converse com um especialista e receba uma proposta no mesmo dia.', align: 'center', maxWidth: '560px' }),
          b('ui.buttons', { items: 'Falar com um especialista|#formulario|solid', align: 'center', size: 'lg' }),
        ],
      ),
    ],
  },

  {
    key: 'cta.clara',
    category: 'cta',
    label: 'Convite claro',
    description: 'Fundo claro, título curto e dois botões.',
    preview: 'cta',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '860px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' }, [
        b('ui.title', { text: 'Vamos conversar sobre o seu caso', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.buttons', { items: 'Pedir proposta|#formulario|solid\nVer planos|#planos|outline', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'cta.com-foto',
    category: 'cta',
    label: 'Convite com foto',
    description: 'Foto ao lado do convite, com texto de apoio.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1080px', motion: 'fade-up' }, [
        b('ui.title', { text: 'A próxima etapa é simples', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Deixe o seu contato e retornamos em poucos minutos, no horário comercial.' }),
        b('ui.buttons', { items: 'Quero ser atendido|#formulario|solid', size: 'lg' }),
        b('ui.image', { src: FOTO.atendimento, alt: 'Atendimento ao cliente', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
      ]),
    ],
  },

  {
    key: 'cta.sobre-foto',
    category: 'cta',
    label: 'Convite sobre a foto',
    description: 'Foto de fundo escurecida com chamada em destaque.',
    preview: 'banner',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.cidadeNoite, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '900px', motion: 'fade' },
        [
          b('ui.title', { text: 'Sua operação pode render mais', size: 'xl', level: 'h2', align: 'center' }),
          b('ui.buttons', { items: 'Começar agora|#formulario|glass', align: 'center', size: 'lg' }),
        ],
      ),
    ],
  },

  {
    key: 'cta.com-prazo',
    category: 'cta',
    label: 'Convite com prazo',
    description: 'Faixa de validade acima da chamada para ação.',
    preview: 'cta',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '900px', motion: 'fade-up' }, [
        b('page.deadline', { title: 'Condição válida até o fim do mês', note: 'Depois desta data os valores podem mudar.' }),
        b('ui.title', { text: 'Garanta a condição deste mês', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.buttons', { items: 'Quero aproveitar|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'cta.com-prova-social',
    category: 'cta',
    label: 'Convite com prova social',
    description: 'Fotos de clientes e nota média reforçam o convite.',
    preview: 'cta',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '880px', backgroundGradient: 'soft', radius: 'xl', motion: 'fade' }, [
        b('ui.title', { text: 'Junte-se a mais de 2.000 empresas', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.avatars', { items: AVATARES, text: '+2.000 clientes atendidos', note: 'Nota 4,9 de 5 em avaliações', align: 'center' }),
        b('ui.buttons', { items: 'Quero fazer parte|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'cta.dois-caminhos',
    category: 'cta',
    label: 'Dois caminhos',
    description: 'Dois cartões com convites diferentes lado a lado.',
    preview: 'grid-2',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.card', { icon: '💬', title: 'Falar com um especialista', text: 'Ideal se você ainda tem dúvidas.', variant: 'solid', radius: 'xl', shadow: 'md' }),
        b('ui.card', { icon: '⚡', title: 'Começar agora', text: 'Ideal se você já sabe o que precisa.', variant: 'outline', radius: 'xl', shadow: 'sm' }),
        b('ui.buttons', { items: 'Falar agora|#formulario|solid\nComeçar|#planos|outline', align: 'center' }),
      ]),
    ],
  },

  {
    key: 'cta.com-numero',
    category: 'cta',
    label: 'Convite com resultado',
    description: 'Um número de impacto ao lado do botão.',
    preview: 'cta',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '900px', pattern: 'dots', motion: 'fade' }, [
        b('ui.stat', { value: '+38%', label: 'de propostas aceitas pelos clientes', align: 'center' }),
        b('ui.buttons', { items: 'Quero esse resultado|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'cta.faixa-simples',
    category: 'cta',
    label: 'Faixa simples',
    description: 'Convite compacto para encaixar entre seções.',
    preview: 'banner',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '820px', motion: 'fade' }, [
        b('page.cta', { title: 'Pronto para começar?', subtitle: 'Respondemos em poucos minutos.', buttonLabel: 'Falar agora', buttonHref: '#formulario' }),
      ]),
    ],
  },

  {
    key: 'cta.whatsapp',
    category: 'cta',
    label: 'Convite por WhatsApp',
    description: 'Chamada focada em atendimento rápido por mensagem.',
    preview: 'cta',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '820px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Atendimento rápido', variant: 'soft', align: 'center' }),
        b('ui.title', { text: 'Prefere conversar por mensagem?', size: 'md', level: 'h2', align: 'center' }),
        b('ui.buttons', { items: 'Chamar no WhatsApp|#contato|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
